import { Inject, Injectable, Optional, Renderer2, RendererFactory2, DOCUMENT } from '@angular/core';

import { FACEBOOK_PIXEL_CONFIG, FacebookPixelConfig } from './facebook-pixel.config';
import { CookieConsentService } from '@shared/services/cookie-consent.service';

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class FacebookPixelService {
  private renderer: Renderer2;
  private initialized = false;

  constructor(
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(FACEBOOK_PIXEL_CONFIG) private config: FacebookPixelConfig,
    private readonly _cookieConsent: CookieConsentService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    if (this.config && this.config.enabled && this.config.pixelId) {
      this._cookieConsent
        .grantedOnce$('advertisement')
        .subscribe(() => this.init(this.config.pixelId));
    }
  }

  private init(pixelId: string): void {
    if (this.initialized) {
      return;
    }

    // Initialize fbq function (standard Facebook Pixel snippet logic)
    const win = this.document.defaultView as Window;
    if (win.fbq) {
      return;
    }

    const fbq: any = function (...args: any[]) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue.push(args);
      }
    };

    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    win.fbq = fbq;
    win._fbq = fbq;

    // Inject the Facebook Pixel script tag
    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(script, 'async', '');
    this.renderer.setAttribute(script, 'src', 'https://connect.facebook.net/en_US/fbevents.js');
    this.renderer.appendChild(this.document.head, script);

    // Initialize with the pixel ID and fire a PageView
    win.fbq('init', pixelId);
    win.fbq('track', 'PageView');

    this.initialized = true;
  }

  track(event: string, properties?: object): void {
    if (!this.initialized) {
      return;
    }

    const win = this.document.defaultView as Window;
    if (win.fbq) {
      if (properties) {
        win.fbq('track', event, properties);
      } else {
        win.fbq('track', event);
      }
    }
  }
}
